// Shared draggable, resizable, non-blocking modal shell — generalizes the
// pattern OrderNotesWindow.jsx introduced: react-rnd (Rnd) wrapping a plain
// MUI Paper, no Dialog/backdrop, so the rest of the page stays interactive
// while this is open. Used for every dialog in this app EXCEPT the two
// destructive/safety confirmations (TemplatesPage.jsx's DeleteConfirmDialog,
// FlowsPage.jsx's FlowToggleConfirmDialog) — those stay standard blocking MUI
// Dialogs on purpose, forcing an in-place decision.
//
// Deliberately Dialog-shaped (open/onClose) so swapping <Dialog>...
// <DialogTitle>/<DialogContent>/<DialogActions> for
// <DraggableModal title=.../ actions=...> is a wrapper swap at each call
// site, not a rewrite of the dialog's own logic.
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Rnd } from 'react-rnd'
import { Paper, Box, Typography, IconButton } from '@mui/material'

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

// Each already-open instance offsets the next one's default position by this
// much (x and y) so opening several in a row doesn't stack them identically
// — same convention OrderNotesWindow.jsx introduced, and the same constant
// value, so its multi-instance Notes-window behavior is unchanged.
const STAGGER_PX = 24
// Shared across every instance — react-rnd scopes dragHandleClassName
// lookups to each Rnd's OWN children, so multiple simultaneously-open
// instances never cross-drag each other (already proven by OrderNotesWindow,
// which reuses one class name the same way today).
const DRAG_HANDLE_CLASS = 'draggable-modal-drag-handle'

// open/onClose: standard Dialog-shaped API — renders nothing when !open.
// title: header text/node. children: content area (scrollable, like
// DialogContent) — contentSx merges onto its own sx, for a caller that needs
// to override the default padding (e.g. a custom full-bleed sub-layout).
// actions: optional footer row (like DialogActions) — the row is omitted
// entirely when not provided. headerActions: optional node(s) rendered in
// the header, before the close button (e.g. a copy-all button) — for a
// dialog whose one meaningful action belongs next to the title, not in a
// separate footer row. width/height: initial size in px (number) or any CSS
// size string (e.g. '90vw') — react-rnd accepts either. minWidth/minHeight:
// resize floor. offsetIndex: this instance's position in a currently-open
// list, for staggering multiple simultaneous instances (see
// OrderNotesWindow.jsx) — defaults to 0 for the common single-instance case.
// centered: true computes the default x/y from width/height and the
// viewport size instead of the fixed 120,120 corner — only meaningful when
// width/height are numbers (a CSS size string like '90vw' falls back to the
// corner default, since there's no viewport-relative size to center from
// here). Defaults to false so every existing caller is unaffected; the
// stagger offset still applies on top of the centered base.
// onResizeStop: optional, called with the new { width, height } in px when
// the person finishes a resize (View calls keeps its last size, 2026-10-29).
export default function DraggableModal({
  open, onClose, title, children, actions, contentSx, headerActions,
  width = 480, height = 360, minWidth = 280, minHeight = 200, offsetIndex = 0, centered = false, testId = undefined,
  onResizeStop = undefined,
}) {
  // Escape-to-close, for parity with standard MUI Dialog behavior — this
  // shell has no backdrop/focus trap of its own, so this is attached at the
  // window level regardless of what has focus inside.
  useEffect(() => {
    if (!open) return
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  const offset = offsetIndex * STAGGER_PX
  const baseX = centered && typeof width === 'number' ? Math.max(0, Math.round((window.innerWidth - width) / 2)) : 120
  const baseY = centered && typeof height === 'number' ? Math.max(0, Math.round((window.innerHeight - height) / 2)) : 120

  // Portaled to the document body: react-draggable (which Rnd wraps)
  // positions itself via a CSS transform, and per the CSS spec a transform
  // on ANY ancestor becomes the containing block for a position: fixed
  // descendant — so rendering this at whatever depth a caller happens to be
  // nested in (e.g. inside another position:fixed panel) would silently
  // reposition it relative to that ancestor instead of the viewport. A
  // portal guarantees this is always a direct child of <body>, with no
  // transformed/positioned ancestor in the way, regardless of the caller's
  // own position in the component tree.
  return createPortal(
    <Rnd
      default={{ x: baseX + offset, y: baseY + offset, width, height }}
      minWidth={minWidth}
      minHeight={minHeight}
      dragHandleClassName={DRAG_HANDLE_CLASS}
      bounds="window"
      onResizeStop={onResizeStop ? (_e, _dir, ref) => onResizeStop({ width: ref.offsetWidth, height: ref.offsetHeight }) : undefined}
      style={{ position: 'fixed', zIndex: 1300 }}
    >
      {/* role="dialog" so this is still discoverable the same way a real
          MUI Dialog is (getByRole('dialog') in tests, screen readers) —
          aria-modal is explicitly false since, unlike a real Dialog, this
          never blocks interaction with the rest of the page. */}
      <Paper
        elevation={8}
        role="dialog"
        aria-modal="false"
        aria-label={typeof title === 'string' ? title : undefined}
        data-testid={testId}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}
      >
        <Box
          className={DRAG_HANDLE_CLASS}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: 'action.hover', cursor: 'move', flexShrink: 0 }}
        >
          <Typography variant="subtitle2" noWrap sx={{ pr: 1, flex: 1, minWidth: 0 }}>{title}</Typography>
          {headerActions}
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0, ...contentSx }}>
          {children}
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Paper>
    </Rnd>,
    document.body
  )
}
